/**
 * Shared primitives for Performance drill-down pages.
 *
 * - ACTION_ICON / ACTION_COLORS — visual map for AI action types
 * - ActionCard / ActionRecommendations — MCP-seeded action sidebar
 * - KpiTile — uniform KPI strip cell
 * - DrillDownHeader — back-button + title chrome
 */
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Eye,
  FileText,
  MessageCircle,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import type { WorkbenchFormulaAction } from '@/services/api';
import type { ChatSeedInput } from './EmbeddedChatPanel';

export const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export const COMPACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

export const NUMBER = new Intl.NumberFormat('en-US');

export const ACTION_ICON: Record<string, JSX.Element> = {
  budget: <TrendingUp size={14} className="text-teal-500" />,
  brief: <FileText size={14} className="text-indigo-500" />,
  diagnose: <AlertTriangle size={14} className="text-amber-500" />,
  coverage: <Eye size={14} className="text-slate-500" />,
};

export const ACTION_COLORS: Record<string, string> = {
  budget: 'border-teal-200 bg-teal-50/50',
  brief: 'border-indigo-200 bg-indigo-50/50',
  diagnose: 'border-amber-200 bg-amber-50/50',
  coverage: 'border-slate-200 bg-slate-50',
};

export function ActionCard({
  action,
  onActivate,
  active,
}: {
  action: WorkbenchFormulaAction;
  onActivate: (seed: ChatSeedInput) => void;
  active: boolean;
}) {
  return (
    <div
      className={clsx(
        'border rounded-xl p-4 space-y-2.5 transition-all',
        ACTION_COLORS[action.type] ?? 'border-slate-200 bg-white',
        active && 'ring-2 ring-teal-400',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="mt-0.5 shrink-0">{ACTION_ICON[action.type]}</div>
        <div className="min-w-0">
          <div className="text-[12px] font-bold text-slate-800 leading-snug">{action.title}</div>
        </div>
      </div>
      <p className="text-[11px] text-slate-600 leading-relaxed">{action.rationale}</p>
      <button
        onClick={() =>
          onActivate({
            question: action.chat_seed.question,
            chart_axis: action.chat_seed.chart_axis,
          })
        }
        className={clsx(
          'w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-semibold transition-colors',
          active
            ? 'bg-teal-500 text-white'
            : 'bg-white border border-slate-200 text-slate-700 hover:border-teal-400 hover:text-teal-700',
        )}
      >
        <MessageCircle size={11} />
        {active ? 'Analyst open ↓' : 'Open in analyst'}
        {!active && <ArrowRight size={10} />}
      </button>
    </div>
  );
}

export function ActionRecommendations({
  actions,
  onActivate,
  activeSeed,
  emptyText = 'No recommendations yet — more data needed.',
}: {
  actions: WorkbenchFormulaAction[];
  onActivate: (seed: ChatSeedInput) => void;
  activeSeed: ChatSeedInput | null;
  emptyText?: string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles size={13} className="text-teal-500" />
        <h2 className="text-[12px] font-bold text-navy-900">Recommended Actions</h2>
        <span className="text-[10px] text-slate-400">powered by Customer Agent MCP</span>
      </div>
      <p className="text-[11px] text-slate-500">
        Each recommendation is derived from the real data on this page. Click any to open
        a pre-loaded analyst session.
      </p>
      <div className="space-y-3">
        {actions.map((action) => (
          <ActionCard
            key={action.id}
            action={action}
            onActivate={onActivate}
            active={activeSeed?.question === action.chat_seed.question}
          />
        ))}
        {actions.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-4">{emptyText}</div>
        )}
      </div>
    </div>
  );
}

export interface KpiTileProps {
  label: string;
  value: string;
  sub?: string;
  icon?: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}

const TONE_VALUE: Record<NonNullable<KpiTileProps['tone']>, string> = {
  default: 'text-navy-900',
  success: 'text-teal-700',
  warning: 'text-amber-700',
  danger: 'text-red-600',
};

export function KpiTile({ label, value, sub, icon, tone = 'default' }: KpiTileProps) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-1">
      <div className="flex items-center justify-between text-slate-400 mb-1">
        <span className="text-[10px] font-mono uppercase tracking-wide text-slate-500">
          {label}
        </span>
        {icon}
      </div>
      <div className={clsx('text-xl font-bold truncate', TONE_VALUE[tone])} title={value}>
        {value}
      </div>
      {sub && (
        <p className="text-[10px] text-slate-500 truncate" title={sub}>
          {sub}
        </p>
      )}
    </div>
  );
}

export function DrillDownHeader({
  breadcrumb,
  title,
  icon,
  caption,
}: {
  breadcrumb: string;
  title: string;
  icon: ReactNode;
  caption?: string;
}) {
  const navigate = useNavigate();
  return (
    <header className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/creative/performance')}
          className="p-1.5 text-slate-500 hover:text-teal-600 rounded hover:bg-slate-100"
          aria-label="Back to Performance"
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <div className="text-[10px] font-mono uppercase text-slate-400">{breadcrumb}</div>
          <h1 className="text-xl font-bold text-navy-900 flex items-center gap-2">
            {icon}
            {title}
          </h1>
        </div>
      </div>
      {caption && (
        <p className="text-[11px] text-slate-500 italic max-w-sm text-right hidden md:block">
          {caption}
        </p>
      )}
    </header>
  );
}
